import React, { memo, useState } from 'react';
import ModalWithPattern from './ModalWithPattern';
import styled from '@emotion/styled';
import { areEqual, isDarkMode, IsMobileViewPort } from '../../../utils/helpers';
import { getBackgroundTheme, getTextColor } from '../../../style';
import PrimaryButton from '../../../atoms/buttons/PrimaryButton';
import colors, { white } from '../../../theme/colors';
import PriceInput from '../../pages/Offers/PriceInput';
import typography from '../../../theme/typography';
import borders from '../../../theme/borders';
import { getDefaultOfferAmount } from '../../../utils/offers';
import { defaultOfferAmount } from '../../../common';
import { CustomStyledProps } from 'style/style';
import { FONT_FAMILIES } from 'theme/material/constants';
import { SvgIcon } from '@material-ui/core';
import { CloseOutlined } from '@material-ui/icons';
import SecondaryButton from 'atoms/buttons/SecondaryButton';
import { PriceContainer } from 'components/pages/Offers/common';

const InitialInputProps = {
  style: {
    fontSize: 30,
    height: 45,
    lineHeight: '21px',
    fontWeight: 700,
    outline: 'none',
    border: 'none',
    marginLeft: -17,
    letterSpacing: '-0.72px',
    fontFamily: FONT_FAMILIES.primary,
    backgroundColor: white,
  },
};

const validateTextInput = (e: any) => {
  const Key = e.key;
  const regex = /[0-9]/;

  if (!regex.test(Key)) {
    if (e.preventDefault) e.preventDefault();
  }
};

const setColor = () => ({
  default: {
    color: 'grey',
  },
  brand: {
    color: '#3e79a5',
  },
  alert: { ...getTextColor().RED },
});

const Heading1 = styled('h6')(() => {
  const theme = isDarkMode() ? 'dark' : 'light';

  return {
    width: '100%',
    fontSize: 20,
    fontWeight: 400,
    fontFamily: FONT_FAMILIES.secondary,
    color: typography[theme].heading,
    letterSpacing: '-0.2px',
    lineHeight: '20px',
    margin: 0,
  };
});

const LineSeparator = styled('hr')(() => {
  const theme = isDarkMode() ? 'dark' : 'light';

  return {
    borderColor: borders[theme].primary,
    marginTop: 10,
    marginBottom: 20,
    width: '100%',
  };
});

const CurrencyTip = styled('div')(
  {
    display: 'inline-block',
    position: 'relative',
    paddingLeft: 10,
  },
  ({ offerPrice }: CustomStyledProps) => ({
    ...setColor()[offerPrice],
    ':before': {
      position: 'absolute',
      top: 14,
      right: '85%',
      fontSize: 16,
    },
  })
);

const ButtonContainer = styled('div')({
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const OfferAmountLabel = styled('label')(() => {
  const theme = isDarkMode() ? 'dark' : 'light';

  return {
    width: '100%',
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '19px',
    letterSpacing: '-0.3px',
    color: typography[theme].primaryText,
  };
});

const OpeningOfferPriceContainer = styled(PriceContainer)(() => ({
  height: 45,
  marginBottom: 20,
}));

const CurrencyLabelSpan = styled('span')(() => ({
  fontSize: 30,
  fontWeight: 700,
  color: isDarkMode() ? colors.blue[200] : colors.light.blue.secondary,
  letterSpacing: '-0.6px',
  lineHeight: '21px',
  fontFamily: FONT_FAMILIES.secondary,
}));

const mobileOverlayStyle = {
  borderRadius: 6,
  height: '100%',
  width: '90%',
  padding: '0 10%',
  left: '-5%',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
};

const mobileContentStyle = {
  borderRadius: 6,
  maxHeight: 600,
  top: '50%',
  height: 'auto',
  transform: 'translate(0, -50%)',
  border: '0',
  background: isDarkMode() ? '#212121' : white,
};

const offerPriceEmptyStyle = {
  paddingLeft: 0,
  width: '100%',
  textAlign: 'center',
  marginLeft: 0,
};

interface EditableOpeningOfferModalProps {
  title: any;
  handleCancel: any;
  handleSubmit: any;
  currencySymbols: any;
  offerPrice: any;
  setOfferPrice: any;
  toggleEditOpeningOffer: boolean;
}

const EditableOpeningOfferModal = ({
  title,
  handleCancel,
  handleSubmit,
  currencySymbols,
  offerPrice,
  setOfferPrice,
  toggleEditOpeningOffer,
}: EditableOpeningOfferModalProps): JSX.Element => {
  const [hasErrors, setHasErrors] = useState(false);
  const minValue = defaultOfferAmount.minValue;
  const maxValue = defaultOfferAmount.maxValue;

  const inputOnChange = e => {
    const inputValue = e.target.value;
    const MaxLength = maxValue.toString().length;

    if (inputValue === '' || inputValue.length <= MaxLength) {
      const isInvalid =
        inputValue !== '' && Boolean(Number(inputValue) < Number(minValue) || Number(inputValue) > Number(maxValue));

      setOfferPrice(inputValue);
      setHasErrors(isInvalid);
    } else {
      e.preventDefault();
    }
  };

  const colorType = (price: boolean, hasErrors: boolean) => {
    switch (price) {
      case true:
        return 'default';
      case false:
        return !hasErrors ? 'brand' : 'alert';
      default:
        return;
    }
  };

  return (
    // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
    <ModalWithPattern
      dataTestID="editable-opening-offer-modal"
      isModalOpen={toggleEditOpeningOffer}
      modalContentLabel="Editable Opening Offer Modal"
      closeBtnClassName="first-date-offer-tutorial-close"
      closeBtnDataTestId="first-date-offer-tutorial-close"
      onCloseClick={handleCancel}
      customOverlayStyle={IsMobileViewPort ? mobileOverlayStyle : {}}
      customContentStyle={
        IsMobileViewPort
          ? mobileContentStyle
          : { maxWidth: 349, borderRadius: 10, background: getBackgroundTheme().block2 }
      }
      customContentBodyStyle={{ padding: '15px' }}
      hideCloseBtn
    >
      <Heading1>{title}</Heading1>
      <LineSeparator />
      <OfferAmountLabel>{`Enter a new default offer amount`}</OfferAmountLabel>
      <OpeningOfferPriceContainer
        // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
        hasErrors={hasErrors}
      >
        {offerPrice !== '' && (
          <CurrencyLabelSpan style={{ paddingLeft: 44 }}>
            {currencySymbols.currencyInitial}
            {currencySymbols.currencyLabel}
          </CurrencyLabelSpan>
        )}
        <CurrencyTip
          offerPrice={colorType(offerPrice, hasErrors)}
          style={{
            paddingLeft: 0,
          }}
        >
          <PriceInput
            data-test-id="dashboard-discovermode-offer-price-input"
            hasErrors={hasErrors}
            value={offerPrice}
            onChange={inputOnChange}
            validateTextFn={validateTextInput}
            placeholder={`${currencySymbols.currencyInitial}${currencySymbols.currencyLabel}${getDefaultOfferAmount()}`}
            inputProps={{
              style: {
                ...InitialInputProps.style,
                marginLeft: 3,
                textAlign: 'left',
              },
            }}
            minWidth={100}
            // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
            overideInputStyle={offerPrice === '' ? offerPriceEmptyStyle : { paddingLeft: 0 }}
            responsiveFontSize={`
              color: ${isDarkMode() ? colors.blue[200] : colors.light.blue.secondary};
              `}
          />
        </CurrencyTip>
      </OpeningOfferPriceContainer>

      <ButtonContainer>
        <SecondaryButton
          size="x-small"
          color="blue"
          onClick={handleCancel}
          data-test-id="editable-textarea-cancel-btn"
          label="Cancel"
          withIcon={<SvgIcon component={CloseOutlined} style={{ width: 16, height: 16, cursor: 'pointer' }} />}
          customStyle={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '4px',
            width: '100px',
            lineHeight: '16px',
          }}
        />
        <PrimaryButton
          color="red"
          size="x-small"
          data-test-id="close-preview"
          onClick={handleSubmit}
          label="Save"
          customStyle={{ width: 70, borderRadius: '4px' }}
          disabled={hasErrors}
        />
      </ButtonContainer>
    </ModalWithPattern>
  );
};

export default memo(EditableOpeningOfferModal, areEqual);
